import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../../contexts/AuthContext';
import { paymentConfigService } from '../../services/paymentConfigService';
import { describeMerchantGatewayAppId, getUniqueMerchantConfigs } from '../../utils/paymentConfig';
import { hasMenuPermission } from '../../utils/permissions';
import {
  PAYMENT_FORM_AUTO_COMPLETE,
  PAYMENT_INPUT_AUTO_COMPLETE,
  PAYMENT_PASSWORD_AUTO_COMPLETE,
  PaymentFormAutofillGuard,
} from './paymentFormAutocomplete';
import { showPaymentSaveWarnings } from './paymentSaveWarnings';
import {
  MerchantConfig,
  MerchantConfigSaveRequest,
  PAYMENT_PROVIDER_MAP,
  PaymentProvider,
} from '../../types';

const { Paragraph, Title, Text } = Typography;

const PAYMENT_PROVIDER_OPTIONS: PaymentProvider[] = [
  'WECHAT_OFFICIAL',
  'YEEPAY',
  'ALIPAY_OFFICIAL',
  'YUNHUITENG',
];

const getProviderLabel = (provider?: PaymentProvider) => (provider ? PAYMENT_PROVIDER_MAP[provider] || provider : '-');

export const WECHAT_PAY_PUBLIC_KEY_ID_PREFIX = 'PUB_KEY_ID_';
export const WECHAT_PAY_PUBLIC_KEY_PEM_MARKER = '-----BEGIN PUBLIC KEY-----';

export const getMerchantSecretFields = (provider?: PaymentProvider) => {
  if (provider === 'WECHAT_OFFICIAL') {
    return [
      'apiKey',
      'merchantPrivateKey',
      'merchantCertificateSerialNo',
      'merchantCertificateContent',
      'wechatPayPublicKeyId',
      'wechatPayPublicKey',
    ];
  }
  if (provider === 'YEEPAY') {
    return ['merchantNo', 'subMerchantNo', 'apiKey'];
  }
  if (provider === 'ALIPAY_OFFICIAL') {
    return ['sellerId', 'appAuthToken'];
  }
  if (provider === 'YUNHUITENG') {
    return ['merchantNo', 'privateKey'];
  }
  return [];
};

export const getMerchantConfigGuidance = (provider?: PaymentProvider) => {
  if (provider === 'WECHAT_OFFICIAL') {
    return '微信新支付路由使用微信支付 V3 JSAPI 下单。必须填写 API v3 key、商户API证书私钥 apiclient_key.pem、商户API证书序列号；商户证书内容 apiclient_cert.pem 不能替代私钥，只用于辅助校验证书序列号。在微信商户平台 → 账户中心 → API 安全 → 微信支付公钥 下载公钥并复制公钥ID。申请后需开启公钥切换。旧小程序支付链路仍使用旧商户号配置，不受这里影响。';
  }
  if (provider === 'ALIPAY_OFFICIAL') {
    return '支付宝官方支付的应用私钥、公钥或证书请在“应用资质配置”中维护；默认签约账号收款时，不需要在这里新增收款账号也可以正常支付。只有多收款账号或服务商代调用场景，才需要填写 sellerId 或 appAuthToken。';
  }
  if (provider === 'YUNHUITENG') {
    return '一条云汇腾商户配置对应一个子商户：同一商户号下的多个子商户请分别新增，各自填写云汇腾应用 ID 与 MD5 业务私钥。查询接口仅返回私钥已配置状态或脱敏摘要，不会回显明文；留空私钥表示不更新已有密文。';
  }
  return '';
};

export const normalizeOptionalSecret = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export const getWechatPayPublicKeyPairErrors = (
  id?: string,
  pem?: string,
): { wechatPayPublicKeyId?: string; wechatPayPublicKey?: string } => {
  const normalizedId = normalizeOptionalSecret(id);
  const normalizedPem = normalizeOptionalSecret(pem);
  if (!normalizedId && !normalizedPem) {
    return {};
  }
  const errors: { wechatPayPublicKeyId?: string; wechatPayPublicKey?: string } = {};
  if (!normalizedId || !normalizedId.startsWith(WECHAT_PAY_PUBLIC_KEY_ID_PREFIX)) {
    errors.wechatPayPublicKeyId = '请填写微信支付公钥ID，形如 PUB_KEY_ID_xxx';
  }
  if (!normalizedPem) {
    errors.wechatPayPublicKey = '请同时填写微信支付公钥，需包含 -----BEGIN PUBLIC KEY-----';
  } else if (!normalizedPem.includes(WECHAT_PAY_PUBLIC_KEY_PEM_MARKER)) {
    errors.wechatPayPublicKey = '请粘贴 pub_key.pem 完整内容，需包含 -----BEGIN PUBLIC KEY-----';
  }
  return errors;
};

export const removeEmptySecrets = (values: MerchantConfigSaveRequest): MerchantConfigSaveRequest => {
  const next = { ...values };
  ([
    'apiKey',
    'merchantPrivateKey',
    'merchantCertificateSerialNo',
    'merchantCertificateContent',
    'appAuthToken',
    'privateKey',
    'wechatPayPublicKeyId',
    'wechatPayPublicKey',
  ] as const).forEach(key => {
    if (!normalizeOptionalSecret(next[key])) {
      delete next[key];
    }
  });
  if (next.paymentProvider !== 'WECHAT_OFFICIAL') {
    delete next.wechatPayPublicKeyId;
    delete next.wechatPayPublicKey;
  }
  const gatewayAppId = normalizeOptionalSecret(next.gatewayAppId);
  if (next.paymentProvider === 'YUNHUITENG' && gatewayAppId) {
    next.gatewayAppId = gatewayAppId;
  } else {
    delete next.gatewayAppId;
  }
  delete next.wechatPayPublicKeyMask;
  delete next.wechatPayPublicKeyConfigured;
  delete next.effectiveGatewayAppId;
  return next;
};

const PaymentMerchantConfig: React.FC = () => {
  const { menus, permissions } = useAuth();
  const canSaveConfig = hasMenuPermission(menus, permissions, 'PAYMENT_CONFIG', 'WRITE');
  const canUpdateSecrets = hasMenuPermission(menus, permissions, 'PAYMENT_SECRET', 'WRITE');
  const canMaintain = canSaveConfig || canUpdateSecrets;
  const [configs, setConfigs] = useState<MerchantConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MerchantConfig | null>(null);
  const [form] = Form.useForm<MerchantConfigSaveRequest>();
  const paymentProvider = Form.useWatch('paymentProvider', form);

  const providerOptions = useMemo(
    () => PAYMENT_PROVIDER_OPTIONS.map(value => ({ label: getProviderLabel(value), value })),
    [],
  );

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentConfigService.queryPaymentConfig();
      if (res.status !== 1) {
        message.error(res.msg || '获取商户/收款账号配置失败');
        return;
      }
      setConfigs(getUniqueMerchantConfigs(res.data?.platforms || []));
    } catch {
      message.error('获取商户/收款账号配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    if (editingRecord) {
      form.resetFields();
      form.setFieldsValue({
        merchantConfigId: editingRecord.merchantConfigId,
        paymentProvider: editingRecord.paymentProvider,
        merchantNo: editingRecord.merchantNo,
        subMerchantNo: editingRecord.subMerchantNo,
        sellerId: editingRecord.sellerId,
        name: editingRecord.name,
        gatewayAppId: editingRecord.gatewayAppId,
      });
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      paymentProvider: 'WECHAT_OFFICIAL',
    });
  }, [modalOpen, editingRecord, form]);

  const openAddModal = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const openEditModal = (record: MerchantConfig) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = removeEmptySecrets({
        ...values,
        merchantConfigId: editingRecord?.merchantConfigId,
      });
      const res = await paymentConfigService.saveMerchantConfigs([payload]);
      if (res.status === 1) {
        message.success('商户/收款账号配置已保存');
        showPaymentSaveWarnings(res.data);
        closeModal();
        await loadConfigs();
      } else {
        message.error(res.msg || '商户/收款账号配置保存失败');
      }
    } catch {
      // 表单校验错误不提示，避免重复打扰。
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<MerchantConfig> = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '支付服务商',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      width: 170,
      render: (value: PaymentProvider) => <Tag color="blue">{getProviderLabel(value)}</Tag>,
    },
    {
      title: '商户号',
      dataIndex: 'merchantNo',
      key: 'merchantNo',
      width: 180,
      render: (value: string) => <Text copyable>{value || '-'}</Text>,
    },
    {
      title: '云汇腾应用 ID',
      key: 'gatewayAppId',
      width: 200,
      render: (_: unknown, record: MerchantConfig) => {
        if (record.paymentProvider !== 'YUNHUITENG') {
          return '-';
        }
        const { value, inherited } = describeMerchantGatewayAppId(record);
        if (!value) {
          return <Tag color="orange">未填写</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            <Text copyable code>{value}</Text>
            {inherited && <Text type="secondary">来自应用资质，建议补填</Text>}
          </Space>
        );
      },
    },
    {
      title: '支付宝收款账号',
      dataIndex: 'sellerId',
      key: 'sellerId',
      width: 180,
      render: (value: string) => <Text copyable>{value || '-'}</Text>,
    },
    {
      title: '私钥/Token',
      key: 'secretConfigured',
      width: 120,
      render: (_: unknown, record: MerchantConfig) => {
        if (record.paymentProvider === 'YUNHUITENG') {
          return (
            <Tag color={record.privateKeyConfigured ? 'green' : 'default'}>
              {record.privateKeyConfigured ? '私钥已配置' : '私钥未配置'}
            </Tag>
          );
        }
        if (record.paymentProvider === 'ALIPAY_OFFICIAL') {
          return (
            <Tag color={record.appAuthTokenConfigured ? 'green' : 'default'}>
              {record.appAuthTokenConfigured ? 'Token 已配置' : 'Token 未配置'}
            </Tag>
          );
        }
        return <Tag color="default">-</Tag>;
      },
    },
    {
      title: '配置完整性',
      dataIndex: 'configComplete',
      key: 'configComplete',
      width: 120,
      render: (value: boolean) => <Tag color={value ? 'green' : 'red'}>{value ? '完整' : '缺失'}</Tag>,
    },
    {
      title: '敏感字段状态',
      key: 'sensitiveStatus',
      render: (_: unknown, record: MerchantConfig) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">{record.sensitiveStatus?.summary || '-'}</Text>
          {record.sensitiveStatus?.updatedAt && (
            <Text type="secondary">更新时间：{record.sensitiveStatus.updatedAt}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_: unknown, record: MerchantConfig) => (
        <Tooltip title={!canMaintain ? '缺少支付配置维护权限' : ''}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            disabled={!canMaintain}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={3}>商户/收款账号配置</Title>
            <Paragraph type="secondary">
              维护微信商户号、易宝商户参数、云汇腾子商户（商户号、云汇腾应用 ID 与私钥）、支付宝 sellerId 或服务商授权 Token。
            </Paragraph>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={loadConfigs}>
              刷新
            </Button>
            <Tooltip title={!canMaintain ? '缺少支付配置维护权限' : ''}>
              <Button type="primary" icon={<PlusOutlined />} disabled={!canMaintain} onClick={openAddModal}>
                新增商户/收款账号
              </Button>
            </Tooltip>
          </Space>
        </Space>
        {!canMaintain && (
          <Alert type="warning" showIcon message="当前账号没有支付配置维护权限，可查看但不可保存。" />
        )}
      </Card>

      <Card>
        <Table
          rowKey={record => String(record.merchantConfigId)}
          columns={columns}
          dataSource={configs}
          loading={loading}
          scroll={{ x: 1300 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑商户/收款账号配置' : '新增商户/收款账号配置'}
        open={modalOpen}
        confirmLoading={saving}
        onCancel={closeModal}
        onOk={handleSubmit}
        okText="保存"
        cancelText="取消"
        width={880}
        destroyOnClose
      >
        <Form form={form} layout="vertical" autoComplete={PAYMENT_FORM_AUTO_COMPLETE}>
          <PaymentFormAutofillGuard />
          {(paymentProvider === 'ALIPAY_OFFICIAL' ||
            paymentProvider === 'WECHAT_OFFICIAL' ||
            paymentProvider === 'YUNHUITENG') && (
            <Alert
              type="info"
              showIcon
              message={
                paymentProvider === 'WECHAT_OFFICIAL'
                  ? '微信 V3 商户配置说明'
                  : paymentProvider === 'YUNHUITENG'
                    ? '云汇腾商户配置说明'
                    : '支付宝默认收款说明'
              }
              description={getMerchantConfigGuidance(paymentProvider)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentProvider" label="支付服务商" rules={[{ required: true, message: '请选择支付服务商' }]}>
                <Select options={providerOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="配置名称" rules={[{ required: true, message: '请输入配置名称' }]}>
                <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="例如：默认微信商户号" />
              </Form.Item>
            </Col>
            {paymentProvider !== 'ALIPAY_OFFICIAL' && (
              <Col span={12}>
                <Form.Item
                  name="merchantNo"
                  label={
                    paymentProvider === 'YEEPAY'
                      ? '易宝商编'
                      : paymentProvider === 'YUNHUITENG'
                        ? '云汇腾商户号'
                        : '微信商户号'
                  }
                  rules={
                    paymentProvider === 'YUNHUITENG'
                      ? [{ required: true, message: '请输入云汇腾商户号' }]
                      : undefined
                  }
                >
                  <Input
                    autoComplete={PAYMENT_INPUT_AUTO_COMPLETE}
                    placeholder={
                      paymentProvider === 'YEEPAY'
                        ? '请输入易宝商编'
                        : paymentProvider === 'YUNHUITENG'
                          ? '请输入云汇腾 mchNo'
                          : '请输入微信 mchId'
                    }
                  />
                </Form.Item>
              </Col>
            )}
            {paymentProvider === 'YUNHUITENG' && (
              <Col span={12}>
                <Form.Item
                  name="gatewayAppId"
                  label="云汇腾应用 ID"
                  rules={
                    editingRecord
                      ? undefined
                      : [{ required: true, whitespace: true, message: '请输入云汇腾应用 ID' }]
                  }
                  extra={
                    editingRecord?.gatewayAppId
                      ? '已被路由引用时不能修改；如需更换请新建商户配置后在支付路由中切换。'
                      : editingRecord?.effectiveGatewayAppId
                        ? `当前下单使用应用资质中的 ${editingRecord.effectiveGatewayAppId}，补填后以这里为准。`
                        : '子商户在云汇腾侧的应用标识（gatewayAppId），不是微信小程序 AppID。'
                  }
                >
                  <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="请输入云汇腾 gatewayAppId" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('subMerchantNo') && (
              <Col span={12}>
                <Form.Item name="subMerchantNo" label="易宝子商户号">
                  <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="按易宝渠道配置填写，可为空" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('sellerId') && (
              <Col span={12}>
                <Form.Item name="sellerId" label="支付宝收款账号">
                  <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="默认签约账号收款时可为空" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('apiKey') && (
              <Col span={12}>
                <Form.Item name="apiKey" label={paymentProvider === 'YEEPAY' ? '渠道商户密钥' : '微信 API v3 key'}>
                  <Input.Password autoComplete={PAYMENT_PASSWORD_AUTO_COMPLETE} placeholder="留空表示不更新" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('merchantCertificateSerialNo') && (
              <Col span={12}>
                <Form.Item name="merchantCertificateSerialNo" label="商户API证书序列号">
                  <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="留空表示不更新" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('wechatPayPublicKeyId') && (
              <Col span={12}>
                <Form.Item
                  name="wechatPayPublicKeyId"
                  label="微信支付公钥 ID"
                  dependencies={['wechatPayPublicKey']}
                  extra={
                    editingRecord?.wechatPayPublicKeyId
                      ? `已配置：${editingRecord.wechatPayPublicKeyId}`
                      : undefined
                  }
                  rules={[
                    {
                      validator: async (_, value) => {
                        const errors = getWechatPayPublicKeyPairErrors(
                          value,
                          form.getFieldValue('wechatPayPublicKey'),
                        );
                        if (errors.wechatPayPublicKeyId) {
                          return Promise.reject(new Error(errors.wechatPayPublicKeyId));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    autoComplete={PAYMENT_INPUT_AUTO_COMPLETE}
                    placeholder={
                      editingRecord?.wechatPayPublicKeyId ? '不修改请留空' : '形如 PUB_KEY_ID_xxx'
                    }
                  />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('appAuthToken') && (
              <Col span={12}>
                <Form.Item name="appAuthToken" label="支付宝服务商授权 Token">
                  <Input.Password autoComplete={PAYMENT_PASSWORD_AUTO_COMPLETE} placeholder="留空表示不更新" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('privateKey') && (
              <Col span={12}>
                <Form.Item
                  name="privateKey"
                  label="云汇腾业务私钥"
                  extra={
                    editingRecord?.privateKeyConfigured
                      ? '已配置脱敏摘要可见；留空表示不更新现有密文。'
                      : '新建时必填；查询不会回显明文。'
                  }
                  rules={
                    editingRecord?.privateKeyConfigured
                      ? undefined
                      : [{ required: true, message: '请输入云汇腾业务私钥' }]
                  }
                >
                  <Input.Password autoComplete={PAYMENT_PASSWORD_AUTO_COMPLETE} placeholder="留空表示不更新" />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('merchantPrivateKey') && (
              <Col span={12}>
                <Form.Item name="merchantPrivateKey" label="商户API证书私钥">
                  <Input.TextArea
                    rows={8}
                    autoComplete={PAYMENT_INPUT_AUTO_COMPLETE}
                    placeholder="填写 apiclient_key.pem 完整内容，留空表示不更新"
                  />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('merchantCertificateContent') && (
              <Col span={12}>
                <Form.Item name="merchantCertificateContent" label="商户API证书内容">
                  <Input.TextArea
                    rows={8}
                    autoComplete={PAYMENT_INPUT_AUTO_COMPLETE}
                    placeholder="填写 apiclient_cert.pem 完整内容，可留空"
                  />
                </Form.Item>
              </Col>
            )}
            {getMerchantSecretFields(paymentProvider).includes('wechatPayPublicKey') && (
              <Col span={24}>
                <Form.Item
                  name="wechatPayPublicKey"
                  label="微信支付公钥"
                  dependencies={['wechatPayPublicKeyId']}
                  extra={
                    editingRecord?.wechatPayPublicKeyConfigured
                      ? `已配置：${editingRecord.wechatPayPublicKeyMask || '******'}`
                      : undefined
                  }
                  rules={[
                    {
                      validator: async (_, value) => {
                        const errors = getWechatPayPublicKeyPairErrors(
                          form.getFieldValue('wechatPayPublicKeyId'),
                          value,
                        );
                        if (errors.wechatPayPublicKey) {
                          return Promise.reject(new Error(errors.wechatPayPublicKey));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={8}
                    autoComplete={PAYMENT_INPUT_AUTO_COMPLETE}
                    placeholder={
                      editingRecord?.wechatPayPublicKeyConfigured
                        ? '不修改请留空'
                        : '粘贴 pub_key.pem 完整内容，需包含 -----BEGIN PUBLIC KEY-----'
                    }
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </Space>
  );
};

export default PaymentMerchantConfig;
